<template>
  <div>
    <label
      :for="disabled ? undefined : id"
      :class="contextClass"
      class="mb-1 block"
    >
      {{ name }}
      <sup v-if="required" class="top-0 text-sm font-bold text-red-400">*</sup>
    </label>
    <textarea
      :id="id"
      v-model.trim="model"
      rows="4"
      cols="50"
      :disabled="disabled"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :class="className"
      class="block w-full resize-none rounded border border-solid border-gray-600 bg-gray-100 px-2 py-[5px] text-sm focus:border-blue-400"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleChange"
    />
    <span v-show="isError" class="mt-1 block px-2 text-xs text-red-400">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';

  interface IProps {
    id: string;
    name: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    maxlength?: number;
    isError?: boolean;
    errorMessage?: string;
    labelClass?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    required: false,
    disabled: false,
    placeholder: '',
    maxlength: undefined,
    isError: false,
    errorMessage: '',
    labelClass: '',
  });

  const emits = defineEmits<{
    (e: 'blur', value: string): void;
    (e: 'change', value: string): void;
  }>();

  const model = defineModel<string>({ default: '' });

  const isFocus = ref<boolean>(false);

  const className = computed((): string => {
    if (props.disabled) {
      return 'pointer-events-none cursor-not-allowed opacity-40';
    }
    if (props.isError) {
      return 'border-red-400';
    }
    return '';
  });

  const contextClass = computed((): string => {
    const defaultClass = 'mb-1 block text-xs text-gray-600';
    return props.labelClass ? props.labelClass : defaultClass;
  });

  const handleFocus = (): void => {
    isFocus.value = true;
  };

  const handleBlur = (): void => {
    isFocus.value = false;
    emits('blur', model.value);
  };

  const handleChange = (): void => {
    emits('change', model.value);
  };
</script>
